(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory(
      require('./ai-chat-core.js'),
      require('./ai-chat-state.js'),
      null,
    );
    return;
  }

  if (root) {
    root.MarineMaxAIChatQuote = factory(
      root.MarineMaxAIChatCore,
      root.MarineMaxAIChatState,
      root,
    );
  }
})(typeof window !== 'undefined' ? window : null, function (core, state, root) {
  'use strict';

  if (!core) {
    throw new Error('MarineMax AI Chat core is required by the quote module');
  }
  if (!state) {
    throw new Error('MarineMax AI Chat state is required by the quote module');
  }

  const quoteActions = Object.freeze([
    'Schedule a Sea Trial',
    'Apply for Financing',
    'Talk to a Specialist',
  ]);

  function createQuoteController(options = {}) {
    const chatRoot = options.root;
    const stateManager = options.stateManager;
    const client = options.client;
    const navigation = options.navigation;
    const sendMessage = options.sendMessage || (() => {});
    const showChat = options.showChat || (() => {});
    const stockNumber = String(options.stockNumber || '');
    const storeCode = String(options.storeCode || '');
    const productTitle = String(options.productTitle || '');
    const marineMaxLogoUrl = String(options.marineMaxLogoUrl || '');
    const poweredByLogoUrl = String(options.poweredByLogoUrl || '');
    const documentRef =
      options.document ||
      (chatRoot && chatRoot.ownerDocument) ||
      (root && root.document) ||
      null;

    if (!chatRoot || !stateManager || !navigation || !documentRef) {
      throw new Error('AI Chat quote dependencies are incomplete');
    }
    if (
      !client ||
      ['createQuote', 'sendQuoteEmail'].some(
        (methodName) => typeof client[methodName] !== 'function',
      )
    ) {
      throw new Error('An InventoryIQ client is required by the quote module');
    }
    if (!marineMaxLogoUrl || !poweredByLogoUrl) {
      throw new Error('Quote logo asset URLs are required by the quote module');
    }

    const chatBox = chatRoot.querySelector('.ai-chat__chat');
    const backButton = chatRoot.querySelector('.ai-chat__back');
    const suggestionContainer = chatRoot.querySelector('.ai-chat__suggest');
    let currentViewData = null;

    function createElement(tagName, className, textContent) {
      const element = documentRef.createElement(tagName);
      if (className) element.className = className;
      if (textContent !== undefined) element.textContent = textContent;
      return element;
    }

    function createImage(src, alt, width, height) {
      const image = createElement('img');
      image.src = src;
      image.alt = alt;
      image.width = width;
      image.height = height;
      return image;
    }

    function createPriceRow(label, value, options = {}) {
      const row = createElement(
        'div',
        `ai-chat__quote-price-row${options.total ? ' ai-chat__quote-price-total' : ''}`,
      );
      row.appendChild(createElement('span', '', label));
      row.appendChild(
        createElement(
          'span',
          options.discount ? 'ai-chat__quote-price-value--discount' : '',
          value,
        ),
      );
      return row;
    }

    function createPreparedFor(buyerName) {
      const container = createElement('div', 'ai-chat__quote-prepared-for');
      container.appendChild(
        createElement('div', 'ai-chat__quote-prepared-for-label', 'PREPARED FOR'),
      );
      container.appendChild(
        createElement('div', 'ai-chat__quote-prepared-for-name', buyerName),
      );
      return container;
    }

    function createPriceBreakdown(quote) {
      const msrp = quote.price || 0;
      const discount = quote.discount || 0;
      const taxes = quote.taxes || 0;
      const fees = quote.fees || 0;
      const total = quote.total || msrp - discount + taxes + fees;
      const breakdown = createElement('div', 'ai-chat__quote-price-breakdown');
      const items = createElement('div', 'ai-chat__quote-price-items');
      items.appendChild(createPriceRow('MSRP:', core.formatCurrency(msrp)));
      items.appendChild(
        createPriceRow('Discount:', `-${core.formatCurrency(discount)}`, {
          discount: true,
        }),
      );
      items.appendChild(createPriceRow('Taxes:', core.formatCurrency(taxes)));
      items.appendChild(createPriceRow('Fees:', core.formatCurrency(fees)));
      breakdown.appendChild(items);
      breakdown.appendChild(createElement('div', 'ai-chat__quote-price-divider'));
      breakdown.appendChild(
        createPriceRow('Total', core.formatCurrency(total), { total: true }),
      );
      return breakdown;
    }

    function createValidity(quote, buyerEmail) {
      const container = createElement('div', 'ai-chat__quote-validity');
      const expirationDate = quote.expiration_date
        ? core.formatDate(quote.expiration_date)
        : 'July 27, 2025';
      const validUntil = createElement(
        'div',
        'ai-chat__quote-validity-line',
        `Valid until ${expirationDate}.`,
      );

      const terms = createElement('div', 'ai-chat__quote-validity-line');
      terms.appendChild(documentRef.createTextNode('This quote is not final. '));
      const termsLink = createElement(
        'a',
        'ai-chat__quote-validity-link ai-chat__quote-validity-link--terms',
        'View Terms & Conditions',
      );
      termsLink.href = '#';
      terms.appendChild(termsLink);

      const delivery = createElement('div', 'ai-chat__quote-validity-line');
      delivery.appendChild(documentRef.createTextNode("We'll send this to "));
      const emailLink = createElement(
        'a',
        'ai-chat__quote-validity-link ai-chat__quote-validity-link--email',
        buyerEmail,
      );
      emailLink.href = `mailto:${buyerEmail}`;
      delivery.appendChild(emailLink);

      container.appendChild(validUntil);
      container.appendChild(terms);
      container.appendChild(delivery);
      return container;
    }

    function createContact() {
      const contact = createElement('div', 'ai-chat__quote-contact');
      contact.innerHTML = `
        <div class="ai-chat__quote-contact-title">Contact</div>
        <div class="ai-chat__quote-contact-text">MarineMax Miami</div>
        <div class="ai-chat__quote-contact-row">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M4.38373 4.94911C4.84772 5.91552 5.48025 6.82128 6.2813 7.62234C7.08235 8.42339 7.98812 9.05591 8.95453 9.51991C9.03765 9.55982 9.07922 9.57978 9.13181 9.59511C9.31871 9.64959 9.54822 9.61046 9.70651 9.49712C9.75105 9.46522 9.78915 9.42712 9.86536 9.35091C10.0984 9.11784 10.215 9.0013 10.3322 8.92511C10.7741 8.63778 11.3438 8.63778 11.7857 8.92511C11.9029 9.00131 12.0194 9.11784 12.2525 9.35091L12.3824 9.48082C12.7367 9.83512 12.9139 10.0123 13.0101 10.2025C13.2015 10.5809 13.2015 11.0277 13.0101 11.4061C12.9139 11.5964 12.7367 11.7735 12.3824 12.1278L12.2773 12.2329C11.9243 12.586 11.7477 12.7625 11.5077 12.8974C11.2414 13.047 10.8277 13.1545 10.5222 13.1536C10.2469 13.1528 10.0588 13.0994 9.68247 12.9926C7.66021 12.4186 5.75197 11.3356 4.15998 9.74366C2.56799 8.15167 1.48501 6.24343 0.911032 4.22116C0.804227 3.84487 0.750824 3.65672 0.750006 3.38142C0.749097 3.07594 0.856671 2.66228 1.00628 2.39594C1.14112 2.15592 1.31766 1.97938 1.67074 1.6263L1.77583 1.52121C2.13012 1.16691 2.30727 0.989765 2.49753 0.893535C2.8759 0.702155 3.32274 0.702155 3.70112 0.893535C3.89137 0.989765 4.06852 1.16691 4.42281 1.52121L4.55273 1.65112C4.7858 1.88419 4.90233 2.00073 4.97852 2.11791C5.26585 2.55984 5.26585 3.12956 4.97852 3.57149C4.90233 3.68867 4.7858 3.80521 4.55273 4.03828C4.47652 4.11449 4.43841 4.15259 4.40652 4.19713C4.29318 4.35542 4.25405 4.58492 4.30853 4.77183C4.32386 4.82442 4.34381 4.86598 4.38373 4.94911Z" stroke="#74777F" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span class="ai-chat__quote-contact-text">(305) 555-1234</span>
        </div>
        <div class="ai-chat__quote-contact-row">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M14.334 12L9.90541 7.99996M6.09589 7.99996L1.66734 12M1.33398 4.66663L6.77726 8.47692C7.21804 8.78547 7.43843 8.93974 7.67816 8.9995C7.88991 9.05228 8.11139 9.05228 8.32314 8.9995C8.56287 8.93974 8.78326 8.78547 9.22404 8.47692L14.6673 4.66663M4.53398 13.3333H11.4673C12.5874 13.3333 13.1475 13.3333 13.5753 13.1153C13.9516 12.9236 14.2576 12.6176 14.4493 12.2413C14.6673 11.8135 14.6673 11.2534 14.6673 10.1333V5.86663C14.6673 4.74652 14.6673 4.18647 14.4493 3.75865C14.2576 3.38232 13.9516 3.07636 13.5753 2.88461C13.1475 2.66663 12.5874 2.66663 11.4673 2.66663H4.53398C3.41388 2.66663 2.85383 2.66663 2.426 2.88461C2.04968 3.07636 1.74372 3.38232 1.55197 3.75865C1.33398 4.18647 1.33398 4.74652 1.33398 5.86663V10.1333C1.33398 11.2534 1.33398 11.8135 1.55197 12.2413C1.74372 12.6176 2.04968 12.9236 2.426 13.1153C2.85383 13.3333 3.41388 13.3333 4.53398 13.3333Z" stroke="#74777F" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span class="ai-chat__quote-contact-text">sales@marinemax.com</span>
        </div>`;
      return contact;
    }

    function createPoweredBy() {
      const poweredBy = createElement('div', 'ai-chat__quote-powered-by');
      poweredBy.appendChild(
        createElement('div', 'ai-chat__quote-powered-by-label', 'Powered by'),
      );
      poweredBy.appendChild(
        createImage(poweredByLogoUrl, 'Boatyard', 56, 16),
      );
      return poweredBy;
    }

    function createNextSteps() {
      const nextSteps = createElement('div', 'ai-chat__quote-next-steps');
      nextSteps.appendChild(
        createElement(
          'div',
          'ai-chat__quote-next-steps-message',
          'What would you like to do next?',
        ),
      );
      const buttons = createElement('div', 'ai-chat__quote-next-steps-buttons');
      quoteActions.forEach((action) => {
        const button = createElement('button', 'ai-chat__quote-action-btn', action);
        button.type = 'button';
        buttons.appendChild(button);
      });
      nextSteps.appendChild(buttons);
      nextSteps.addEventListener('click', (event) => {
        const actionButton = event.target.closest('.ai-chat__quote-action-btn');
        if (!actionButton || !nextSteps.contains(actionButton)) return;
        const action = actionButton.textContent.trim();
        if (quoteActions.includes(action)) sendMessage(action);
      });
      return nextSteps;
    }

    function createBackButton() {
      const button = createElement('button', 'ai-chat__quote-back-btn');
      button.type = 'button';
      button.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M13.3334 8H2.66675M6.66675 4L2.66675 8L6.66675 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg> Back to Boat Page';
      button.addEventListener('click', showChat);
      return button;
    }

    async function create(buyerName, buyerEmail, buyerPhone, authToken) {
      const { first, last } = core.splitBuyerName(buyerName);
      return client.createQuote(
        {
          contact_first_name: first,
          contact_last_name: last,
          contact_email: buyerEmail,
          contact_phone: buyerPhone || '',
          buyer_name: buyerName,
          buyer_email: buyerEmail,
          buyer_phone: buyerPhone || '',
          stock_number: stockNumber,
          store_code: storeCode,
        },
        authToken,
      );
    }

    async function sendEmail(quoteId, buyerName, buyerEmail, authToken) {
      return client.sendQuoteEmail(
        quoteId,
        { buyer_name: buyerName, buyer_email: buyerEmail },
        authToken,
      );
    }

    function render(quoteData, buyerName, buyerEmail, options = {}) {
      if (options.savePreviousState !== false) navigation.saveState();
      currentViewData = Object.freeze({
        quoteData: Object.freeze({ ...(quoteData || {}) }),
        buyerName: String(buyerName || ''),
        buyerEmail: String(buyerEmail || ''),
      });
      stateManager.setView(state.views.QUOTE);
      if (!chatBox) return false;

      if (backButton) backButton.style.display = 'flex';
      if (suggestionContainer) suggestionContainer.style.display = 'none';
      navigation.syncHeaderMode();
      chatBox.innerHTML = '';
      chatBox.appendChild(
        createElement(
          'div',
          'ai-chat__quote-success-message',
          "You're all set! 🎉 Here's your personalized quote ↓",
        ),
      );

      const quoteCard = createElement('div', 'ai-chat__quote-card');
      const logo = createElement('div', 'ai-chat__quote-logo');
      logo.appendChild(
        createImage(marineMaxLogoUrl, 'MarineMax', 125, 28),
      );
      quoteCard.appendChild(logo);
      quoteCard.appendChild(createPreparedFor(currentViewData.buyerName));
      quoteCard.appendChild(
        createElement(
          'div',
          'ai-chat__quote-boat-model',
          currentViewData.quoteData.boat_name || productTitle,
        ),
      );
      quoteCard.appendChild(createPriceBreakdown(currentViewData.quoteData));
      quoteCard.appendChild(
        createValidity(currentViewData.quoteData, currentViewData.buyerEmail.trim()),
      );
      quoteCard.appendChild(createContact());
      quoteCard.appendChild(createPoweredBy());
      chatBox.appendChild(quoteCard);
      chatBox.appendChild(createNextSteps());
      chatBox.appendChild(createBackButton());
      chatBox.scrollTop = 0;
      return true;
    }

    function captureViewData() {
      return currentViewData;
    }

    function restoreView(snapshot) {
      if (!snapshot || snapshot.view !== state.views.QUOTE) return false;
      const viewData = snapshot.viewData || {};
      return render(
        viewData.quoteData || {},
        viewData.buyerName || '',
        viewData.buyerEmail || '',
        { savePreviousState: false },
      );
    }

    return Object.freeze({
      captureViewData,
      create,
      ownsView(view) {
        return view === state.views.QUOTE;
      },
      render,
      restoreView,
      sendEmail,
    });
  }

  return Object.freeze({
    createQuoteController,
    quoteActions,
  });
});
